import { useState } from "react";
import {
  Sparkles, User, Users, Star, PieChart, Zap, ArrowRight,
  GlassWater, Coffee, Wine, IceCreamCone, Sandwich, Beef, Fish,
  Soup, Salad, EggFried, Cookie, UtensilsCrossed,
  LucideIcon,
} from "lucide-react";

type IconCmp = LucideIcon;

interface Imprescindible {
  codArticulo: number;
  nombre: string;
  familia: string;
  visitas: number;
  pctVisitas: number;
}

interface Comp {
  familia: string;
  gastoUsd: number;
  unidades: number;
  pctGasto: number;
  pctUnidades: number;
}

interface Combo {
  codX: number; nombreX: string; familiaX: string; vecesX: number;
  codY: number; nombreY: string; familiaY: string;
  vecesJuntos: number; confianzaPct: number;
}

interface ContextoData {
  imprescindibles: Imprescindible[];
  composicion: Comp[];
  combos: Combo[];
}

interface HabitosData {
  conteos: { individuales: number; grupales: number; total: number };
  individual: ContextoData;
  grupo: ContextoData;
}

interface LegacyVc {
  codArticulo: number;
  nombreArticulo: string;
  vecesPedido: number;
  compraJuntoCon: Array<{ codArticulo: number; nombreArticulo: string; vecesJunto: number }>;
}

// Paleta consistente para familias
const PALETA = [
  "#0EA5E9", "#F97316", "#10B981", "#EC4899", "#8B5CF6",
  "#F59E0B", "#06B6D4", "#EF4444", "#84CC16", "#6366F1",
];
function colorFamilia(familia: string): string {
  let h = 0;
  for (let i = 0; i < familia.length; i++) h = (h * 31 + familia.charCodeAt(i)) % 1000;
  return PALETA[h % PALETA.length];
}

function iconoFamilia(familia: string): IconCmp {
  const f = familia.toLowerCase();
  if (/bebid|refresc|gase|jugo|agua|t[eé]/i.test(f)) return GlassWater;
  if (/caf[eé]|exp?reso/i.test(f)) return Coffee;
  if (/cerv|vino|licor|trag|cocktail|c[oó]ctel|whisk|ron|vodka|gin|alcoh/i.test(f)) return Wine;
  if (/post|dulc|torta|helad|cake/i.test(f)) return IceCreamCone;
  if (/pan|empan|hamb|hotdog|sandw|pizza|past|fideo|risott|spaghetti/i.test(f)) return Sandwich;
  if (/pollo|carn|res|cerd|costill|filet|chicharr|chorizo|parrill/i.test(f)) return Beef;
  if (/pesc|mar|cev|ostra|camar|atun|salmon|merluza/i.test(f)) return Fish;
  if (/sopa|caldo|crema/i.test(f)) return Soup;
  if (/ensal|verdura|vegetal/i.test(f)) return Salad;
  if (/desayun|huev|tortill|arepa/i.test(f)) return EggFried;
  if (/snack|aper|picad|entrada|tequ|ñoki/i.test(f)) return Cookie;
  return UtensilsCrossed;
}

function FamiliaIcon({ familia, size = 16, className = "" }: { familia: string; size?: number; className?: string }) {
  const Icon = iconoFamilia(familia);
  return <Icon size={size} strokeWidth={1.8} className={className} aria-hidden />;
}

export function HabitosCruzadosCard({
  data,
  legacy,
}: {
  data: HabitosData | null | undefined;
  legacy: LegacyVc[];
}) {
  const [tab, setTab] = useState<"individual" | "grupo">(
    (data?.conteos?.grupales ?? 0) > (data?.conteos?.individuales ?? 0) ? "grupo" : "individual"
  );

  const activo = tab === "individual" ? data?.individual : data?.grupo;
  const totalCtx = tab === "individual" ? (data?.conteos.individuales ?? 0) : (data?.conteos.grupales ?? 0);
  const tieneData = !!data && (
    (data.individual.imprescindibles.length + data.individual.composicion.length + data.individual.combos.length +
     data.grupo.imprescindibles.length + data.grupo.composicion.length + data.grupo.combos.length) > 0
  );

  return (
    <div className="card overflow-hidden">
      <div className="flex items-center gap-2 px-5 py-3 border-b border-ink-200">
        <Sparkles className="size-4 text-brand-600" />
        <h2 className="font-display font-bold text-ink-800">Hábitos de consumo</h2>
        {data?.conteos?.total ? (
          <span className="text-xs text-ink-500 ml-auto">
            {data.conteos.total} visita{data.conteos.total === 1 ? "" : "s"}
          </span>
        ) : null}
      </div>

      {!tieneData ? (
        <>
          {legacy.length === 0 ? (
            <div className="p-8 text-center text-ink-400 text-sm">
              Aún no hay suficientes compras para detectar hábitos
            </div>
          ) : (
            <div className="divide-y divide-ink-100">
              {legacy.map((vc) => (
                <div key={vc.codArticulo} className="px-5 py-4">
                  <div className="text-xs text-ink-500 mb-1">Cuando compra</div>
                  <div className="text-sm font-medium text-ink-800 mb-2">{vc.nombreArticulo}</div>
                  <ul className="space-y-1.5">
                    {vc.compraJuntoCon.slice(0, 3).map((o) => (
                      <li key={o.codArticulo} className="text-xs text-ink-600 flex justify-between">
                        <span className="truncate">→ {o.nombreArticulo}</span>
                        <span className="text-ink-400 shrink-0 ml-2">{o.vecesJunto}×</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          {/* Tabs solo / grupo */}
          <div className="grid grid-cols-2 border-b border-ink-200">
            <TabButton active={tab === "individual"} onClick={() => setTab("individual")}
              icon={<User className="size-4" />} label="Cuando viene solo" count={data!.conteos.individuales} />
            <TabButton active={tab === "grupo"} onClick={() => setTab("grupo")}
              icon={<Users className="size-4" />} label="Cuando viene con grupo" count={data!.conteos.grupales} />
          </div>

          {totalCtx === 0 ? (
            <div className="p-8 text-center text-ink-400 text-sm">
              {tab === "individual"
                ? "Aún no tiene visitas individuales registradas"
                : "Aún no tiene visitas con grupo registradas"}
            </div>
          ) : (
            <>
              {/* 1) IMPRESCINDIBLES */}
              <Seccion
                icono={<Star className="size-4 text-amber-500" />}
                titulo="Lo que casi siempre pide"
                subtitulo={`Productos en al menos 30% de sus ${totalCtx} visita${totalCtx === 1 ? "" : "s"}`}
              >
                {activo!.imprescindibles.length === 0 ? (
                  <p className="text-xs text-ink-400 italic px-1">
                    No detectamos un producto repetido lo suficiente — varía mucho cada visita.
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {activo!.imprescindibles.map((p) => (
                      <li key={p.codArticulo} className="text-sm">
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <span className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-lg" style={{ background: `${colorFamilia(p.familia)}1f`, color: colorFamilia(p.familia) }}>
                              <FamiliaIcon familia={p.familia} size={15} />
                            </span>
                            <div className="min-w-0 flex-1">
                              <div className="text-ink-800 font-medium truncate" title={p.nombre}>
                                {p.nombre}
                              </div>
                              <div className="text-[10px] text-ink-400 truncate">{p.familia}</div>
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className="font-bold text-ink-800">{p.pctVisitas}%</div>
                            <div className="text-[10px] text-ink-400">{p.visitas}/{totalCtx} visitas</div>
                          </div>
                        </div>
                        <div className="h-1.5 rounded-full bg-amber-100 overflow-hidden">
                          <div className="h-full bg-amber-500" style={{ width: `${p.pctVisitas}%` }} />
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </Seccion>

              {/* 2) COMPOSICIÓN DEL TICKET */}
              <Seccion
                icono={<PieChart className="size-4 text-emerald-600" />}
                titulo="Cómo gasta su ticket"
                subtitulo="Proporción de gasto por familia"
              >
                {activo!.composicion.length === 0 ? (
                  <p className="text-xs text-ink-400 italic">Sin datos suficientes.</p>
                ) : (
                  <Composicion items={activo!.composicion} />
                )}
              </Seccion>

              {/* 3) COMBOS PREDECIBLES */}
              <Seccion
                icono={<Zap className="size-4 text-indigo-600" />}
                titulo="Combos predecibles"
                subtitulo="Cuando pide A, casi seguro pide B (confianza ≥ 65%)"
              >
                {activo!.combos.length === 0 ? (
                  <p className="text-xs text-ink-400 italic">
                    No tiene combos consistentes todavía — su elección cambia visita a visita.
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {activo!.combos.map((c) => (
                      <li key={`${c.codX}-${c.codY}`} className="rounded-lg border border-indigo-100 bg-indigo-50/40 px-3 py-2 text-sm">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-ink-800 inline-flex items-center gap-1.5">
                            <FamiliaIcon familia={c.familiaX} size={14} className="text-indigo-500" />
                            {c.nombreX}
                          </span>
                          <ArrowRight className="size-3.5 text-indigo-500 shrink-0" />
                          <span className="font-medium text-ink-800 inline-flex items-center gap-1.5">
                            <FamiliaIcon familia={c.familiaY} size={14} className="text-indigo-500" />
                            {c.nombreY}
                          </span>
                          <span className={`ml-auto text-xs font-bold rounded-full px-2.5 py-1 ${
                            c.confianzaPct >= 75 ? "bg-indigo-600 text-white" :
                            c.confianzaPct >= 50 ? "bg-amber-100 text-amber-800 border border-amber-300" :
                            "bg-rose-100 text-rose-700 border border-rose-300"
                          }`}>
                            {c.confianzaPct}%
                          </span>
                        </div>
                        <p className="text-[11px] text-ink-500 mt-1">
                          {c.vecesJuntos} de {c.vecesX} veces que pidió "{c.nombreX}" también pidió "{c.nombreY}"
                        </p>
                      </li>
                    ))}
                  </ul>
                )}
              </Seccion>
            </>
          )}
        </>
      )}
    </div>
  );
}

function TabButton({
  active, onClick, icon, label, count,
}: {
  active: boolean; onClick: () => void; icon: React.ReactNode; label: string; count: number;
}) {
  return (
    <button onClick={onClick}
      className={`px-3 py-3 flex items-center justify-center gap-1.5 text-sm font-medium border-b-2 transition-colors ${
        active
          ? "border-brand-500 text-brand-700 bg-brand-50/40"
          : "border-transparent text-ink-500 hover:text-ink-700 hover:bg-ink-50"
      }`}>
      {icon}
      <span>{label}</span>
      <span className={`text-xs px-1.5 py-0.5 rounded-full ${
        active ? "bg-brand-100 text-brand-700" : "bg-ink-100 text-ink-500"
      }`}>{count}</span>
    </button>
  );
}

function Seccion({
  icono, titulo, subtitulo, children,
}: {
  icono: React.ReactNode; titulo: string; subtitulo: string; children: React.ReactNode;
}) {
  return (
    <div className="px-5 py-4 border-b border-ink-100 last:border-b-0">
      <div className="mb-3">
        <div className="flex items-center gap-2 mb-0.5">
          {icono}
          <h3 className="font-semibold text-ink-800 text-sm">{titulo}</h3>
        </div>
        <p className="text-[11px] text-ink-500">{subtitulo}</p>
      </div>
      {children}
    </div>
  );
}

function Composicion({ items }: { items: Comp[] }) {
  // Barra apilada + lista detallada
  const totalGasto = items.reduce((s, i) => s + i.gastoUsd, 0);
  return (
    <div>
      {/* Barra apilada */}
      <div className="h-3 rounded-full overflow-hidden bg-ink-100 flex mb-3">
        {items.map((i) => (
          <div key={i.familia}
            title={`${i.familia}: ${i.pctGasto}%`}
            style={{ width: `${i.pctGasto}%`, backgroundColor: colorFamilia(i.familia) }} />
        ))}
      </div>
      {/* Lista */}
      <ul className="space-y-1.5">
        {items.map((i) => (
          <li key={i.familia} className="flex items-center gap-2 text-sm">
            <span className="inline-block w-3 h-3 rounded-sm shrink-0"
              style={{ backgroundColor: colorFamilia(i.familia) }} />
            <FamiliaIcon familia={i.familia} size={14} className="shrink-0 text-ink-500" />
            <span className="text-ink-700 truncate flex-1" title={i.familia}>{i.familia}</span>
            <span className="text-ink-500 text-xs">
              ${i.gastoUsd.toFixed(2)}
            </span>
            <span className="font-bold text-ink-800 w-10 text-right">{i.pctGasto}%</span>
          </li>
        ))}
      </ul>
      {totalGasto > 0 && (
        <p className="text-[10px] text-ink-400 mt-2 text-right">
          Gasto total considerado: ${totalGasto.toFixed(2)}
        </p>
      )}
    </div>
  );
}
